/**
 * GENERATED from prototype/index.html.
 * Regenerate with: node tools/gen-data.mjs
 *
 * These are the same numbers prototype/verify.js asserts and the Django
 * seeder loads (backend/apps/game/management/commands/seed_gamedata.py).
 * Do not hand-edit — change the prototype, regenerate, re-run both suites.
 */
import type { AxeLevel, LampLevel, Material, RockTier, RarityDef, Rarity } from './types';

export const RARITIES: readonly RarityDef[] = [
  { key:"common", name:"Common", color:"#9AA3AD", sellMultiplier:1 },
  { key:"uncommon", name:"Uncommon", color:"#5CCB6A", sellMultiplier:1.15 },
  { key:"rare", name:"Rare", color:"#4FA3F7", sellMultiplier:1.35 },
  { key:"epic", name:"Epic", color:"#A86BF0", sellMultiplier:1.6 },
  { key:"legendary", name:"Legendary", color:"#F5A02A", sellMultiplier:2 },
  { key:"mythic", name:"Mythic", color:"#FF5FA2", sellMultiplier:3 },
] as const;

export const RARITY: Readonly<Record<Rarity, RarityDef>> = Object.freeze(
  Object.fromEntries(RARITIES.map((r) => [r.key, r])) as Record<Rarity, RarityDef>,
);

/** The axe gate. A weaker axe bounces off — see docs/GAME_DATA.md. */
export const MATERIALS: readonly Material[] = [
  { key:"loose", name:"Loose Stone", reqAxe:1, hp:1, lootBias:0, colorA:"#6E5240", colorB:"#2C1E16" },
  { key:"hard", name:"Hard Stone", reqAxe:2, hp:1.35, lootBias:0.12, colorA:"#7A736C", colorB:"#2A2724" },
  { key:"granite", name:"Granite", reqAxe:3, hp:1.8, lootBias:0.26, colorA:"#8C6E6A", colorB:"#33241F" },
  { key:"basalt", name:"Basalt", reqAxe:4, hp:2.4, lootBias:0.42, colorA:"#4C5560", colorB:"#161A1F" },
  { key:"obsidian", name:"Obsidian", reqAxe:5, hp:3.1, lootBias:0.6, colorA:"#3E3856", colorB:"#120F1C" },
  { key:"quartz", name:"Quartz Vein", reqAxe:6, hp:4, lootBias:0.82, colorA:"#9FA7C7", colorB:"#2A2E45" },
  { key:"adamant", name:"Adamant", reqAxe:7, hp:5.2, lootBias:1.1, colorA:"#4E7F72", colorB:"#12261F" },
  { key:"void", name:"Void Rock", reqAxe:8, hp:6.8, lootBias:1.45, colorA:"#3A2C4E", colorB:"#0D0813" },
  { key:"star", name:"Star Stone", reqAxe:9, hp:8.6, lootBias:1.9, colorA:"#6E5AA8", colorB:"#1B1230" },
  { key:"shwe", name:"Shwe Stone", reqAxe:10, hp:11, lootBias:2.5, colorA:"#C89B3C", colorB:"#3B2A0C" },
] as const;

/**
 * How material hardness follows depth. The server owns the real roll
 * (`run_engine.roll_material`); these are the same constants so the HUD can
 * predict it. `saturationDepthM` caps the *material*, never the depth — the cave
 * is endless, the rock is not.
 */
export const MATERIAL_CURVE = {
  saturationDepthM: 1400,
  exponent: 0.62,
  /** Below this everything is the softest material, whatever the curve says. */
  floorDepthM: 14,
} as const;

/** The drop table. Orthogonal to material: any tier can appear in any rock. */
export const ROCK_TIERS: readonly RockTier[] = [
  { key:"trash_rock", label:"Trash Rock", type:"trash", weight:76, coinMult:1 },
  { key:"rich_rock", label:"Rich Rock", type:"trash", weight:18, coinMult:2.6 },
  { key:"treasure_rock", label:"Treasure Rock", type:"treasure", weight:4.4, coinMult:1, pool:"rare" },
  { key:"crystal_rock", label:"Crystal Rock", type:"treasure", weight:1.3, coinMult:1.15, pool:"epic" },
  { key:"ancient_rock", label:"Ancient Rock", type:"treasure", weight:0.28, coinMult:1.35, pool:"legendary" },
  { key:"divine_rock", label:"Divine Rock", type:"treasure", weight:0.02, coinMult:1.6, pool:"mythic" },
] as const;

export const AXES: readonly AxeLevel[] = [
  { lvl:1, name:"Wood Axe", icon:"🪓", dmg:10, crit:3, lootBonus:0, cost:0 },
  { lvl:2, name:"Stone Axe", icon:"⛏", dmg:14, crit:4, lootBonus:2, cost:400 },
  { lvl:3, name:"Copper Axe", icon:"⛏", dmg:19, crit:6, lootBonus:4, cost:1200 },
  { lvl:4, name:"Iron Axe", icon:"⛏", dmg:26, crit:8, lootBonus:7, cost:3200 },
  { lvl:5, name:"Steel Axe", icon:"⛏", dmg:35, crit:10, lootBonus:10, cost:7500 },
  { lvl:6, name:"Gold Axe", icon:"⛏", dmg:48, crit:13, lootBonus:14, cost:16000 },
  { lvl:7, name:"Diamond Axe", icon:"⛏", dmg:66, crit:16, lootBonus:19, cost:34000 },
  { lvl:8, name:"Crystal Axe", icon:"⛏", dmg:90, crit:20, lootBonus:25, cost:70000 },
  { lvl:9, name:"Mythic Axe", icon:"⛏", dmg:124, crit:25, lootBonus:32, cost:150000 },
  { lvl:10, name:"Shwe Axe", icon:"⚒️", dmg:180, crit:32, lootBonus:45, cost:320000 },
] as const;

/** `range: -1` on the last lamp is the endless sentinel — the cave has no bottom. */
export const LAMPS: readonly LampLevel[] = [
  { lvl:1, name:"Candle", icon:"🕯️", range:40, light:170, rareBonus:0, cost:0 },
  { lvl:2, name:"Oil Lamp", icon:"🪔", range:90, light:205, rareBonus:5, cost:600 },
  { lvl:3, name:"Lantern", icon:"🏮", range:170, light:245, rareBonus:11, cost:1800 },
  { lvl:4, name:"Torch", icon:"🔦", range:300, light:290, rareBonus:18, cost:5000 },
  { lvl:5, name:"Crystal Lamp", icon:"💠", range:430, light:345, rareBonus:26, cost:14000 },
  { lvl:6, name:"Magic Lamp", icon:"🧞", range:550, light:405, rareBonus:36, cost:38000 },
  { lvl:7, name:"Golden Lamp", icon:"🏆", range:700, light:480, rareBonus:50, cost:90000 },
  { lvl:8, name:"Spirit Lamp", icon:"👻", range:870, light:540, rareBonus:58, cost:260000 },
  { lvl:9, name:"Eternal Lamp", icon:"♾️", range:1100, light:600, rareBonus:66, cost:700000 },
  { lvl:10, name:"Shwe Lamp", icon:"🌟", range:-1, light:680, rareBonus:75, cost:1800000 },
] as const;

export const axeAt  = (lvl: number): AxeLevel  => AXES[Math.min(Math.max(lvl,1), AXES.length) - 1]!;
export const lampAt = (lvl: number): LampLevel => LAMPS[Math.min(Math.max(lvl,1), LAMPS.length) - 1]!;
